import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Database, Trash2, AlertTriangle } from "lucide-react";

const labels = {
  office_bearers: "Office Bearers", districts: "Districts", grs: "GRs & Notices",
  gapl_teams: "GAPL Teams", gapl_fixtures: "GAPL Fixtures", ads: "Ad Slots",
  sponsors: "Sponsors", upcoming: "Upcoming Events", issues: "Issues",
};

export const OverviewSection = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => adminApi.overview().then(setData);
  useEffect(() => { load(); }, []);

  const removeSample = async () => {
    setBusy(true);
    try {
      const r = await adminApi.removeSampleData();
      const total = Object.values(r.removed).reduce((a, b) => a + b, 0);
      toast.success(`Removed ${total} sample records.`);
      load();
    } catch { toast.error("Failed to remove sample data"); }
    finally { setBusy(false); }
  };

  if (!data) return <p className="text-gmoaa-text-muted">Loading…</p>;

  return (
    <div>
      <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">Overview</h2>
      {data.signature_uploaded === false && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-gmoaa-red/40 bg-gmoaa-red-bg p-3 text-sm font-medium text-gmoaa-red" data-testid="overview-signature-warning">
          <AlertTriangle size={16} /> Treasurer signature not uploaded — receipts print a blank signature line. Add it in Important Bar → Treasurer signature.
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {Object.entries(labels).map(([k, label]) => (
          <div key={k} className="rounded-xl border border-gmoaa-border-subtle bg-white p-4">
            <p className="font-serif text-2xl font-bold text-gmoaa-green">{data[k] ?? 0}</p>
            <p className="text-xs text-gmoaa-text-muted">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-3 rounded-xl border border-dashed border-gmoaa-red/40 bg-gmoaa-red-bg p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Database size={22} className="mt-0.5 shrink-0 text-gmoaa-red" />
          <div>
            <p className="font-semibold text-gmoaa-text-dark">Remove all sample data</p>
            <p className="text-sm text-gmoaa-text-muted">Deletes every record still flagged as sample ({data.sample_total ?? 0} remaining). Your real data is kept.</p>
          </div>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button data-testid="remove-sample-btn" variant="destructive" disabled={busy || !data.sample_total}>
              <Trash2 size={16} className="mr-1.5" /> Remove sample
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gmoaa-cream">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-gmoaa-red"><AlertTriangle size={20} /> Confirm removal</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gmoaa-text-muted">This permanently deletes all remaining sample records. This cannot be undone. Continue?</p>
            <DialogFooter>
              <Button data-testid="remove-sample-confirm" variant="destructive" onClick={removeSample} disabled={busy}>Yes, remove sample data</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
